import type { ExecutiveSummary } from '@/components/dashboard/dashboardWidgets';
import { useExecutiveSummary } from './useExecutiveSummary';
import { type DateFilter } from './useDashboardStats';

export interface RevenueDataPoint {
  month: string;
  revenue: number;
}

export type RevenueChartData = RevenueDataPoint[];

/**
 * Trim leading & trailing zero-revenue buckets so a wide window (e.g. "All time",
 * which can span decades of empty buckets before the business existed) charts
 * only its active span — otherwise the line hugs the axis under a wall of zeros.
 */
export function trimZeroEnds(points: RevenueDataPoint[]): RevenueChartData {
  let start = 0;
  let end = points.length - 1;
  while (start <= end && points[start].revenue === 0) start += 1;
  while (end >= start && points[end].revenue === 0) end -= 1;
  return start > end ? [] : points.slice(start, end + 1);
}

/** A daily bucket key is 'YYYY-MM-DD' (10 chars); monthly is 'YYYY-MM' (7). */
function isDailyKey(period: string | undefined): boolean {
  return (period?.length ?? 0) >= 10;
}

/**
 * Map the executive-summary trend to chart points. DAILY windows (a week/month
 * the user explicitly picked) keep their full axis so the range reads in context
 * — only WIDE monthly windows get zero-ends trimmed to their active span.
 */
export function toRevenueSeries(trend: ExecutiveSummary['revenueTrend']): RevenueChartData {
  const points = (trend ?? []).map((p) => ({
    month: p.period,
    revenue: Number(p.value ?? 0),
  }));
  return isDailyKey(points[0]?.month) ? points : trimZeroEnds(points);
}

function selectRevenueChart(summary: ExecutiveSummary): RevenueChartData {
  return toRevenueSeries(summary.revenueTrend);
}

/**
 * Monthly revenue trend for the Home chart — sourced from the shared executive
 * summary query (same key as the KPIs), so KPIs and the chart share one fetch.
 */
export function useRevenueChart(filter: DateFilter = { period: 'this_week' }) {
  return useExecutiveSummary<RevenueChartData>(filter, { select: selectRevenueChart });
}
